import React, { type ComponentProps, forwardRef } from 'react';
import type { TextInstance } from 'react-native';
import { StyleSheet } from 'react-native';

import { TYPE_CONFIG, WEIGHT_MAP } from '@garnish/constants';

import { useResponsive } from '../../../hooks/useResponsive';
import { Text } from '../Text';

// ─────────────────────────────────────────────────────────────────────────────

export const DisplayWideText = forwardRef<TextInstance, DisplayWideTextProps>((props, ref) => {
  const {
    children,
    sizeMatch = DISPLAY_WIDE_TEXT_DEFAULT_SIZE_MATCH,
    bold,
    style,
    ...restProps
  } = props;

  const { match } = useResponsive();

  const fontSize = match(sizeMatch);
  const textStyles = DISPLAY_WIDE_TEXT_STYLES[fontSize] ?? DISPLAY_WIDE_TEXT_DEFAULT_FONT_STYLES;
  const boldTextStyles = bold ? styles.boldText : undefined;
  const allStyles = [textStyles, boldTextStyles, style];

  return (
    <Text ref={ref} style={allStyles} bold={bold} {...restProps}>
      {children}
    </Text>
  );
});

// ─── Constants ───────────────────────────────────────────────────────────────

const DISPLAY_WIDE_TEXT_STYLES = TYPE_CONFIG.DISPLAY_WIDE;

const DISPLAY_WIDE_TEXT_DEFAULT_SIZE_MATCH: DisplayWideTextFontSize[] = ['40', '64', '80'];
const DISPLAY_WIDE_TEXT_DEFAULT_FONT_SIZE: DisplayWideTextFontSize = '80';

const DISPLAY_WIDE_TEXT_DEFAULT_FONT_STYLES =
  DISPLAY_WIDE_TEXT_STYLES[DISPLAY_WIDE_TEXT_DEFAULT_FONT_SIZE];

// ─── Styles ──────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  boldText: {
    fontWeight: WEIGHT_MAP.bold,
  },
});

// ─── Types ───────────────────────────────────────────────────────────────────

type DisplayWideTextProps = Readonly<{
  sizeMatch?: DisplayWideTextFontSize[];
}> &
  ComponentProps<typeof Text>;

type DisplayWideTextFontSize = keyof typeof DISPLAY_WIDE_TEXT_STYLES;
